import {Group} from 'src/design/atoms/Group';
import {Stack} from 'src/design/atoms/Stack';
import {Text} from 'src/design/atoms/Text';
import type {FileRejection} from '@mantine/dropzone';
import {Dropzone, IMAGE_MIME_TYPE} from '@mantine/dropzone';
import {IconPhoto} from '@tabler/icons-react';
import React, {useEffect, useRef, useState} from 'react';
import type {AttachmentSimpleResponse} from 'src/api/ProactApiClient';
import type {IFormDocumentUploadFile} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {FormFileUploadV2} from 'src/app/forms/FormFileDropzone/FormFileUploadV2';
import {enqueuePhotoCapture} from 'src/app/inspections/offline/photoCapture';
import {useIsInspectionDownloaded} from 'src/app/inspections/offline/useInspectionOfflineSession';
import {useAddInspectionDeviceAttachmentMutation} from 'src/redux/queries/inspectionRecord';
import {useAlert} from 'src/shared/hooks/useAlert';
import {parseErrorMessage} from 'src/shared/utils/parseErrorMessage';
import type {IFormFieldV2} from 'src/types/FormV2';
import {AttachmentGallery} from 'src/pages/Modules/Inspections/shared/AttachmentGallery';

const photosField: IFormFieldV2 = {
  name: 'photos',
  label: 'Photos',
  type: 'file',
  fileUploadOptions: {
    allowMultiple: true,
    maxFileSize: 25e6,
    allowedFileTypes: ['image/*'],
  },
};

export interface DevicePhotosUploaderProps {
  inspectionId: number;
  deviceId: number;
  /** Seeds the uploader so a reopened drawer shows previously uploaded photos. */
  initialAttachments?: AttachmentSimpleResponse[];
  /** Entries opened outside the guided /my flow are always read-only — renders attachments without the dropzone. */
  readOnly?: boolean;
}

const toUploadFile = (
  attachment: AttachmentSimpleResponse
): IFormDocumentUploadFile => ({
  name: attachment.name,
  value: attachment.contentUrl,
  encodedUrl: encodeURIComponent(attachment.contentUrl),
  encodedKey: encodeURIComponent(attachment.key),
  success: true,
});

// Server get-or-creates the stub row — no prior save required.
export const DevicePhotosUploader: React.FC<DevicePhotosUploaderProps> = ({
  inspectionId,
  deviceId,
  initialAttachments,
  readOnly,
}) => {
  const {setAlert} = useAlert();
  const isDownloaded = useIsInspectionDownloaded(inspectionId);
  const [addAttachment] = useAddInspectionDeviceAttachmentMutation();
  const [value, setValue] = useState('[]');
  const [attachedCount, setAttachedCount] = useState(0);
  const [capturedPreviews, setCapturedPreviews] = useState<
    {src: string; alt: string}[]
  >([]);
  const syncedKeysRef = useRef(new Set<string>());
  const seededForDeviceRef = useRef<number | undefined>(undefined);
  const previewUrlsRef = useRef<string[]>([]);

  useEffect(() => {
    if (seededForDeviceRef.current === deviceId) return;
    seededForDeviceRef.current = deviceId;

    // Navigating to another device drops the prior device's previews — revoke
    // their object URLs now rather than leaking them until unmount.
    previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url));
    previewUrlsRef.current = [];

    const seeded = (initialAttachments ?? []).map(toUploadFile);
    syncedKeysRef.current = new Set(seeded.map(file => file.encodedKey));
    setAttachedCount(seeded.length);
    setValue(JSON.stringify(seeded));
    setCapturedPreviews([]);
  }, [deviceId, initialAttachments]);

  useEffect(
    () => () => previewUrlsRef.current.forEach(url => URL.revokeObjectURL(url)),
    []
  );

  const handleChange = async (
    _name: string,
    json: string,
    files: IFormDocumentUploadFile[]
  ) => {
    setValue(json);

    const newFiles = files.filter(
      file => file.success && !syncedKeysRef.current.has(file.encodedKey)
    );

    for (const file of newFiles) {
      syncedKeysRef.current.add(file.encodedKey);
      try {
        await addAttachment({
          inspectionId,
          deviceId,
          request: {
            name: file.name,
            contentUrl: file.value,
            key: decodeURIComponent(file.encodedKey),
          },
        }).unwrap();
        setAttachedCount(count => count + 1);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
    }
  };

  const handleOfflineReject = (rejections: FileRejection[]) => {
    const details = rejections
      .map(
        rejection =>
          `${rejection.file.name} (${rejection.errors
            .map(error => error.message)
            .join(', ')})`
      )
      .join('; ');
    setAlert(`These photos were rejected: ${details}`, 'error');
  };

  const handleOfflineCapture = async (files: File[]) => {
    for (const file of files) {
      try {
        await enqueuePhotoCapture({inspectionId, deviceId, file});
        const src = URL.createObjectURL(file);
        previewUrlsRef.current.push(src);
        setCapturedPreviews(previews => [...previews, {src, alt: file.name}]);
        setAttachedCount(count => count + 1);
      } catch (error) {
        setAlert(parseErrorMessage(error), 'error');
      }
    }
  };

  if (readOnly) {
    const attachments = initialAttachments ?? [];
    return (
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          Photos
        </Text>
        {attachments.length === 0 ? (
          <Text size="sm" c="dimmed">
            No photos
          </Text>
        ) : (
          <AttachmentGallery attachments={attachments} />
        )}
      </Stack>
    );
  }

  if (isDownloaded) {
    return (
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          Photos
        </Text>
        <Dropzone
          accept={IMAGE_MIME_TYPE}
          maxSize={25e6}
          onDrop={handleOfflineCapture}
          onReject={handleOfflineReject}>
          <Group justify="center" gap="sm" mih={80} style={{pointerEvents: 'none'}}>
            <IconPhoto size={24} />
            <Text size="sm" c="dimmed">
              Tap to add photos — saved on this device until you sync
            </Text>
          </Group>
        </Dropzone>
        <AttachmentGallery
          attachments={initialAttachments ?? []}
          extraImages={capturedPreviews}
        />
        {attachedCount > 0 && (
          <Text size="xs" c="dimmed">
            {attachedCount} photo{attachedCount === 1 ? '' : 's'} attached.
          </Text>
        )}
      </Stack>
    );
  }

  return (
    <Stack gap={4}>
      <FormFileUploadV2
        field={photosField}
        value={value}
        onChange={handleChange}
      />
      {attachedCount > 0 && (
        <Text size="xs" c="dimmed">
          {attachedCount} photo{attachedCount === 1 ? '' : 's'} attached.
        </Text>
      )}
    </Stack>
  );
};
